import { useRouter } from 'next/router';
import { Fragment, useEffect } from 'react';
import styled from 'styled-components';
import { mqFrom } from 'styles/styles';
import i18n, { SettingsType } from 'utils/i18n';
import RichText from '@madebyconnor/rich-text-to-jsx';
import { Controller, useWatch, useFieldArray } from 'react-hook-form';
import { numToLetter } from 'utils/helpers';
import ImageWithZoom from 'components/image-with-zoom';
import { isValidString } from 'utils/helpers';

type BlanksQuestionProps = {
  answers: any[];
  answersCurrentLocale: any[];
  question: any;
  completed?: boolean;
  text?: string;
  questionIndex: number;
  control: any;
  feedback?: string;
  image?: any;
  name?: string;
  onAnswerChange?: any;
  questionId?: string;
  questionPoolId?: string;
};

const BlanksQuestion: React.FC<BlanksQuestionProps> = ({
  answers,
  answersCurrentLocale,
  completed,
  question,
  questionIndex,
  control,
  feedback,
  image,
  name = `questions.${questionIndex}.answers`,
  onAnswerChange = null,
  questionId,
  questionPoolId,
}) => {
  const { locale } = useRouter();
  const { dictionary } = i18n[(locale as AcceptedLocales) || 'en-US'] as SettingsType;

  const { fields } = useFieldArray({
    control,
    name: name,
  });

  const selectedAnswers = useWatch({
    control,
    name: name,
    defaultValue: answers.map((_, index) => index),
  });

  useEffect(() => {
    if (questionId && onAnswerChange) {
      onAnswerChange({
        'content-question-id': questionId,
        'content-pool-id': questionPoolId,
        'content-answer-id': selectedAnswers.map((ans, index) => answers[index]?.contentId).join(','),
        correct: selectedAnswers.every(
          (ans, index) => answers.find((a) => a.initialOrder === index)?.initialOrder.toString() === ans.answer
        ),
        name: name,
        answers: answers,
        selectedAnswers: selectedAnswers,
      });
    }
  }, [selectedAnswers]);

  const extractText = (json) => {
    return json.content
      .map((node) => {
        if (node.nodeType === 'paragraph') {
          return node.content.map((contentNode) => contentNode.value).join('');
        }
        return '';
      })
      .join('\n');
  };

  const questionText = extractText(question);
  const parts = questionText.split('{{blank}}');

  return (
    <StyledBlanksQuestion>
      <div className="label">
        <span className="number">{questionIndex + 1}.</span>
        <div className="question-content">
          {parts.map((part, index) => {
            const partWithBreaks = part.split('\n').map((line, lineIndex, array) => (
              <Fragment key={lineIndex}>
                {line}
                {lineIndex < array.length - 1 && (
                  <span className="line-content">
                    <br />
                  </span>
                )}
              </Fragment>
            ));

            return (
              <Fragment key={index}>
                {partWithBreaks}
                {index < parts.length - 1 && (
                  <Controller
                    control={control}
                    name={`${name}.${index}.answer`}
                    render={({ field }) => {
                      const answersCopy = answers.slice();
                      const answersOrdered = answersCopy.sort((a, b) => a.initialOrder - b.initialOrder);
                      const isCorrect =
                        completed && Number(selectedAnswers[index].answer) === answersOrdered[index].initialOrder;
                      const isWrong =
                        completed && Number(selectedAnswers[index].answer) !== answersOrdered[index].initialOrder;

                      const correctAnswer = answers.findIndex(
                        (ans) => Number(selectedAnswers[index].answer) === ans.initialOrder
                      );

                      return (
                        <>
                          <select
                            {...field}
                            className={`select ${completed ? 'no-hover completed' : ''} ${
                              isCorrect ? 'is-correct' : ''
                            } ${isWrong ? 'is-wrong' : ''}`}
                          >
                            <option value=""></option>
                            {answers.map((answer, answerIndex) => (
                              <option key={answerIndex} value={answer.initialOrder}>
                                {numToLetter(answerIndex)}
                              </option>
                            ))}
                          </select>
                          {completed && isWrong && (
                            <p className="correct-container">
                              {numToLetter(
                                answers.findIndex((ans) => answersOrdered[index].initialOrder === ans.initialOrder)
                              )}
                            </p>
                          )}
                        </>
                      );
                    }}
                    rules={{ required: 'This field is required' }}
                  />
                )}
              </Fragment>
            );
          })}
        </div>
      </div>
      <div className="answers">
        <p>Fill in each blank from the options below.</p>
        <ul>
          {answers.map((answer, answerIndex) => {
            const currentLocaleAnswer = answersCurrentLocale?.find(
              (answerLocale) => answerLocale.contentId === answer.contentId
            );

            return (
              <div className="flex" key={answerIndex}>
                <span className="letter">{numToLetter(answerIndex)}.&nbsp;</span>
                <div className="label">
                  <span>
                    {locale !== 'en-US' && isValidString(currentLocaleAnswer.text)
                      ? currentLocaleAnswer.text
                      : answer.text}
                  </span>
                </div>
              </div>
            );
          })}
        </ul>
      </div>
      {/* {completed && feedback && <div className={`message `}>{feedback}</div>} */}
      {image && (
        <div className="image-wrapper">
          <ImageWithZoom src={image.url} alt={image.title} width={image.width} height={image.height} />
        </div>
      )}
    </StyledBlanksQuestion>
  );
};

const StyledBlanksQuestion = styled.div`
  .label {
    display: inline-flex;
    /* flex-direction: column; */

    .number {
      font-weight: 600;
      margin-right: 0.5rem;
    }
  }

  .image-wrapper {
    margin: 1.5rem 0 0 0;
    padding-left: 1.5rem;
    width: 100%;
  }

  .question-content {
    display: inline;
  }

  .answers {
    margin: 1.5rem 3rem 0 1.2rem;

    ul {
      margin-left: 4rem;
    }

    p {
      margin-bottom: 1rem;
    }
  }

  .answer {
    padding: 0.5rem 1rem;
    margin-bottom: 0.5rem;
    position: relative;
    display: flex;
    align-items: center;
    background-color: var(--body-background-medium);
    border: 1px solid transparent;
    border-radius: 0.25rem;
    cursor: pointer;
  }

  .flex {
    display: flex;
    position: relative;
    margin: 0.5rem 0;
  }

  .letter {
    display: flex;
    align-items: center;
    margin-right: 0.4rem;
  }

  .select {
    width: 2rem;
    height: 1.15rem;
    margin-left: 0;
    font-size: 0.7rem;
    text-align: left;
    cursor: pointer;
    border-color: var(--primary-base);
    position: relative;
    top: 0;
    /* -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none; */

    &.completed {
      /* -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none; */
      font-size: 0.7rem;
      text-align: center;
    }

    &.is-correct {
      background-color: var(--success-background);
      border-color: var(--success-base);
      margin-bottom: 0;
    }

    &.is-wrong {
      background-color: var(--danger-background);
      border-color: var(--danger-base);
      margin-bottom: 0;
    }
  }

  .line-content {
    height: 0.5rem;
    width: 100%;
    display: block;
  }

  .correct-container {
    border: 1px solid var(--success-base);
    background-color: var(--success-background);
    width: 2rem;
    height: 18px;
    display: inline-flex;
    text-align: center;
    font-size: 12px;
    color: var(--success-base);
    font-weight: 600;
    justify-content: center;
    align-items: center;
    margin-left: 0.5rem;
  }
`;

export default BlanksQuestion;
